import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Modal, Table } from 'components/Ui'
import { updateApiRecord, deleteApiRecord } from 'services/UsersService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { activeStatus, mfaStatus } from 'components/helper'
import { mfaTypeOptions } from 'utils/options'
import { Button, Select } from 'components/Form'
import * as Yup from 'yup'
import { Field, Form, Formik } from 'formik'
import { apiMFASetup } from 'services/AuthService'
// import dayjs from 'dayjs'
injectReducer('users', reducer)

const validationSchema = Yup.object().shape({
    MFA_type: Yup.string().required('MFA type is required'),
})

export default function Users() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(state => state.users.data)
    const { company, customer } = useSelector(state => state.filterData)
    const [open, setOpen] = useState(false)
    const [email, setEmail] = useState('')
    const [qrCode, setQrCode] = useState('')

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        company,
        customer,
    ])

    const fetchData = () => dispatch(getData({ ...tableData, company: company, customer: customer }))

    const handleSubmit = data => {
        apiMFASetup(data)
            .then(reponse => {
                if (data.MFA_type === 'google_authenticator') {
                    setQrCode(reponse?.data?.qr_code)
                }
                handleStatus(email.id, { mfa_enabled: true })
                fetchData()
            })
            .catch(error => console.log('Error: ', error))
    }

    const handleDelete = id => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch(error => console.log('Error: ', error))
    }

    const handleStatus = (id, data) => {
        updateApiRecord(id, data)
            .then(() => {
                Success('Status Updated Successfully!')
                fetchData()
            })
            .catch(error => console.log('Error: ', error))
    }

    const columns = useMemo(
        () => [
            // {
            //     field: 'company',
            //     header: 'Company',
            //     className: 'min-w-[150px]',
            //     hide: false,
            // },
            {
                field: 'name',
                header: 'Name',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => `${row.first_name} ${row.last_name}`,
            },
            {
                field: 'role',
                header: 'Role',
                className: 'min-w-[150px]',
                hide: false,
                renderCell: row => row.role_data?.[0]?.name,
            },
            {
                field: 'email',
                header: 'Email',
                className: 'min-w-[150px]',
                hide: false,
            },
            // {
            //     field: 'mobile_no',
            //     header: 'Mobile Number',
            //     className: 'min-w-[150px]',
            //     hide: false,
            // },
            {
                field: 'status',
                header: 'Status',
                hide: false,
                renderCell: row => {
                    return checkPermission('users', 'status') ? (
                        <ConfirmationModal
                            onConfirm={() => {
                                handleStatus(row.id, { is_active: !row.is_active })
                            }}
                        >
                            {activeStatus(row.is_active)}
                        </ConfirmationModal>
                    ) : (
                        activeStatus(row.is_active)
                    )
                },
            },
            {
                field: 'mfa_enabled',
                header: 'MFA Status',
                hide: false,
                renderCell: row => {
                    return checkPermission('users', 'edit') ? (
                        row.mfa_enabled ? (
                            <ConfirmationModal
                                onConfirm={() => {
                                    handleStatus(row.id, { mfa_enabled: false })
                                }}
                            >
                                {mfaStatus(row.mfa_enabled)}
                            </ConfirmationModal>
                        ) : (
                            <span
                                className="cursor-pointer"
                                onClick={() => {
                                    setOpen(true)
                                    setEmail(row)
                                }}
                            >
                                {mfaStatus(row.mfa_enabled)}
                            </span>
                        )
                    ) : (
                        mfaStatus(row.is_active)
                    )
                },
            },
            {
                field: 'action',
                header: 'Action',
                className: 'px-4',
                hide: false,
                renderCell: row => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission('users', 'edit') ? (
                                <Tooltip title={'Edit'}>
                                    <Link to={`/company-management/users/${row.id}/edit`} className="hover:text-primary">
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission('users', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={'Are you sure you want to delete this record!'}
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            company,
            customer,
        ]
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Users</h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                datas={data}
                columns={columns}
                add_new={checkPermission('users', 'create') ? '/company-management/users/create' : ''}
                pagingData={tableData}
                onChange={e => dispatch(setTableData(e))}
            />
            <Modal
                show={open}
                onClose={() => {
                    setOpen(false)
                    setQrCode('')
                }}
            >
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">{'MFA Setup'}</h2>
                    <p>Email OTP – Receive a one-time password on registered email.</p>
                    {/* <p>Google Authenticator – Scan QR code and use authenticator app to generate codes.</p> */}
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            {qrCode ? (
                                <div className="flex justify-center items-center">
                                    <img
                                        src={`data:image/png;base64,${qrCode}`}
                                        alt="QR code"
                                        style={{ width: '200px', height: '200px' }}
                                    />
                                </div>
                            ) : (
                                <Formik
                                    initialValues={{ email: email?.email, MFA_type: 'email' }}
                                    validationSchema={validationSchema}
                                    onSubmit={values => {
                                        if (values.MFA_type === 'email') {
                                            setOpen(false)
                                        }
                                        handleSubmit(values)
                                    }}
                                >
                                    {({ values, touched, errors }) => (
                                        <Form>
                                            <div className="grid grid-cols-1 gap-x-4 gap-y-4">
                                                <Field name="MFA_type">
                                                    {({ field, form }) => (
                                                        <Select
                                                            options={mfaTypeOptions}
                                                            label="MFA Type"
                                                            size="sm"
                                                            placeholder="Select"
                                                            name="MFA_type"
                                                            error={errors.MFA_type && touched.MFA_type}
                                                            message={errors.MFA_type}
                                                            value={values?.MFA_type}
                                                            onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                            disabled
                                                        />
                                                    )}
                                                </Field>
                                            </div>
                                            <div className="flex items-center justify-end gap-4 mt-3">
                                                <Button
                                                    className="h-10 w-18 text-[#F05540] border border-[#F05540] dark:text-[#F05540] dark:border-[#F05540]"
                                                    onClick={() => setOpen(false)}
                                                    type="button"
                                                >
                                                    Discard
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-[#F05540] bg-[#F05540] dark:border-[#F05540] dark:bg-[#F05540]"
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}
